import { useAppSelector } from 'app/state/hooks'
import { getMap, getMapDatasets, getMaps, notifySharedUser } from 'features/maps/api/maps'
import { Map, MapComment, MapDataset, RawMapsQueryFilters } from 'features/maps/types'
import { DISABLED_QUERY, queryClient } from 'queryClient'
import { useMutation, useQuery, UseQueryOptions } from 'react-query'
import { getMapAllowedUsers, MapPrivacyProps } from '../model/mapAllowedUsers'
import { CommentFilter } from 'features/collaboration/types'
import { getMapComments } from './mapComments'
import { PaginatedResponse } from 'features/common'

export const MAPS_ROOT_QUERY_KEY = 'maps'
export const mapsQueryKeys = {
  maps: [MAPS_ROOT_QUERY_KEY] as const,
  map: (id: string) => [MAPS_ROOT_QUERY_KEY, id] as const,
  mapDatasets: (id: string) => [MAPS_ROOT_QUERY_KEY, 'datasets', id] as const,
  mapAllowedUsers: (map: MapPrivacyProps) =>
    [MAPS_ROOT_QUERY_KEY, 'allowedUsers', map.id, map.privacy, map.sharingScope, map.users, map.groups] as const,
  mapComments: (mapId?: string, commentFilter?: CommentFilter) =>
    mapId && commentFilter ? [MAPS_ROOT_QUERY_KEY, 'comments', mapId, commentFilter] : [MAPS_ROOT_QUERY_KEY, 'comments']
}

export const getMapQuery = (mapId: string | undefined): UseQueryOptions<Map, Error> => {
  if (!mapId) return DISABLED_QUERY

  return {
    queryKey: mapsQueryKeys.map(mapId),
    queryFn: () => getMap(mapId),
    staleTime: Infinity
  }
}

export const getMapCommentsQuery = (
  mapId: string,
  commentFilter?: CommentFilter
): UseQueryOptions<MapComment[], Error> => {
  return {
    queryKey: mapsQueryKeys.mapComments(mapId, commentFilter),
    queryFn: () => getMapComments(mapId, commentFilter),
    staleTime: Infinity
  }
}
export const queryCacheUpdateMap = (newMap: Partial<Map>) => {
  const mapId = newMap.id
  if (!mapId) return

  queryClient.setQueryData<Map | undefined>(mapsQueryKeys.map(mapId), (oldData: Map | undefined) => {
    if (oldData) {
      return { ...oldData, ...newMap }
    }
  })
}

export const getMapDatasetsQuery = (mapId: string | undefined): UseQueryOptions<MapDataset[], Error> => {
  if (!mapId) return DISABLED_QUERY

  return {
    queryKey: mapsQueryKeys.mapDatasets(mapId),
    queryFn: () => getMapDatasets(mapId),
    staleTime: Infinity
  }
}

export const useNotifySharedUser = () => {
  return useMutation({
    mutationFn: notifySharedUser
  })
}

export const getMapAllowedUsersQuery = (map: Map) => {
  return {
    queryKey: mapsQueryKeys.mapAllowedUsers(map),
    queryFn: () => {
      return getMapAllowedUsers(map)
    }
  }
}

const EMPTY_ARRAY = []
export function useUsersWithAccessToCurrentMap() {
  const currentMap = useAppSelector((state) => state.maps.currentMap)

  const { data: allowedUsers } = useQuery(currentMap ? getMapAllowedUsersQuery(currentMap) : DISABLED_QUERY)
  return allowedUsers || EMPTY_ARRAY
}

export const getMapsQuery = (
  filters: RawMapsQueryFilters,
  noPaginated?: boolean
): UseQueryOptions<PaginatedResponse<Map>, Error> => {
  return {
    queryKey: [MAPS_ROOT_QUERY_KEY],
    queryFn: () => getMaps(filters, noPaginated)
  }
}
